import React from 'react';
import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import { instagramUrl, websiteUrl } from './receiptLinks';
import { getStyles, getDateParts, money, HandleLine } from './receiptPdfShared';

const ThermalReceiptPdf = ({ receipt }) => {
  const { date, time } = getDateParts(receipt?.createdAt);
  const styles = getStyles(receipt?.receiptFontSize);
  const showRate = receipt?.showRateColumn !== false;
  const showAmt = receipt?.showAmountColumn !== false;
  const decimals = receipt?.itemAmountDecimalPlaces || 2;
  const rateLabel = receipt?.rateColumnLabel || 'Rate';
  const amtLabel = receipt?.amountColumnLabel || 'Amt';
  // Hiding a column returns its width to Item rather than leaving blank
  // gutter -- 25%/22% (Rate/Amt) folded back into colItem's 36% as needed.
  const itemColStyle = {
    ...styles.colItem,
    width: `${36 + (showRate ? 0 : 25) + (showAmt ? 0 : 22)}%`,
  };

  return (
    <Document title={`Receipt ${receipt?.billNumber || ''}`}>
      <Page size={[226.77, 900]} style={styles.page}>
        <View style={styles.center}>
          {receipt.logoUrl ? <Image src={receipt.logoUrl} style={styles.logo} /> : null}
          <Text style={styles.storeName}>{receipt.storeName}</Text>
          {receipt.organizationName ? <Text style={styles.orgName}>{receipt.organizationName}</Text> : null}
          {receipt.storeAddress ? <Text style={styles.small}>{receipt.storeAddress}</Text> : null}
          {receipt.storePhone ? <Text style={styles.small}>Mobile: {receipt.storePhone}</Text> : null}
          {receipt.storeEmail ? <Text style={styles.small}>Email: {receipt.storeEmail}</Text> : null}
          {receipt.gstin ? <Text style={styles.small}>GST NO.: {receipt.gstin}</Text> : null}
        </View>

        <View style={styles.divider} />
        <Text style={styles.title}>{receipt.invoiceTitle}</Text>
        <View style={styles.divider} />

        <View>
          <View style={styles.row}><Text style={styles.rowLabel}>Bill No</Text><Text style={styles.rowValue}>{receipt.billNumber}</Text></View>
          <View style={styles.row}><Text style={styles.rowLabel}>Date</Text><Text style={styles.rowValue}>{date}</Text></View>
          <View style={styles.row}><Text style={styles.rowLabel}>Time</Text><Text style={styles.rowValue}>{time}</Text></View>
          {receipt.cashierName ? <View style={styles.row}><Text style={styles.rowLabel}>Cashier</Text><Text style={styles.rowValue}>{receipt.cashierName}</Text></View> : null}
        </View>

        {(receipt.customerName || receipt.customerMobile || receipt.customerAddress) ? (
          <>
            <View style={styles.divider} />
            {receipt.customerName ? <Text>Customer: {receipt.customerName}</Text> : null}
            {receipt.customerMobile ? <Text>Mobile: {receipt.customerMobile}</Text> : null}
            {receipt.customerAddress ? <Text>Address: {receipt.customerAddress}</Text> : null}
          </>
        ) : null}

        <View style={styles.divider} />
        <View style={styles.tableHeader}>
          <Text style={styles.colSr}>Sr</Text>
          <Text style={itemColStyle}>Item</Text>
          <Text style={styles.colQty}>Qty</Text>
          {showRate ? <Text style={styles.colRate}>{rateLabel}</Text> : null}
          {showAmt ? <Text style={styles.colAmt}>{amtLabel}</Text> : null}
        </View>
        {receipt.items.map((item) => (
          <View key={`${item.index}-${item.name}`} style={styles.tableRow}>
            <Text style={styles.colSr}>{item.index}</Text>
            <View style={itemColStyle}>
              <Text>{item.name}</Text>
              {!receipt.hideHsn ? <Text style={styles.subtle}>HSN: {item.hsn || '-'}</Text> : null}
              {(item.color || item.size) ? (
                <Text style={styles.subtle}>
                  {[item.color && `Color: ${item.color}`, item.size && `Size: ${item.size}`].filter(Boolean).join(' · ')}
                </Text>
              ) : null}
              {item.barcode ? <Text style={styles.subtle}>Barcode: {item.barcode}</Text> : null}
              {item.discountAmount > 0 ? (
                item.savingsMessage ? (
                  <Text style={[styles.subtle, styles.savings]}>{item.savingsMessage}</Text>
                ) : (
                  <Text style={styles.subtle}>
                    Discount{item.discountLabel ? ` (${item.discountLabel})` : ''}: {money(item.discountAmount)}
                  </Text>
                )
              ) : null}
            </View>
            <Text style={styles.colQty}>{item.quantity}</Text>
            {showRate ? <Text style={styles.colRate}>{parseFloat(item.rate || 0).toFixed(decimals)}</Text> : null}
            {showAmt ? <Text style={styles.colAmt}>{parseFloat(item.amount || 0).toFixed(decimals)}</Text> : null}
          </View>
        ))}

        <View style={styles.divider} />
        <View style={styles.row}><Text style={styles.rowLabel}>Subtotal</Text><Text style={styles.rowValue}>{money(receipt.subtotal)}</Text></View>
        {receipt.billDiscountAmount > 0 ? (
          <View style={styles.row}>
            <Text style={styles.rowLabel}>Bill Discount{receipt.billDiscountLabel ? ` (${receipt.billDiscountLabel})` : ''}</Text>
            <Text style={styles.rowValue}>{money(receipt.billDiscountAmount)}</Text>
          </View>
        ) : null}
        {receipt.billPromotionDiscountAmount > 0 ? (
          <View style={styles.row}>
            <Text style={styles.rowLabel}>Promotion Discount{receipt.billPromotionDiscountLabel ? ` (${receipt.billPromotionDiscountLabel})` : ''}</Text>
            <Text style={styles.rowValue}>{money(receipt.billPromotionDiscountAmount)}</Text>
          </View>
        ) : null}
        {receipt.discount > 0 && receipt.billDiscountAmount <= 0 && receipt.billPromotionDiscountAmount <= 0 ? (
          receipt.billSavingsMessage ? (
            <View style={styles.row}><Text style={[styles.rowLabel, styles.savings]}>{receipt.billSavingsMessage}</Text></View>
          ) : (
            <View style={styles.row}><Text style={styles.rowLabel}>Discount</Text><Text style={styles.rowValue}>{money(receipt.discount)}</Text></View>
          )
        ) : null}
        {receipt.roundingAdjustment !== 0 ? (
          <View style={styles.row}>
            <Text style={styles.rowLabel}>Rounding</Text>
            <Text style={styles.rowValue}>{receipt.roundingAdjustment > 0 ? '+' : '−'}{money(Math.abs(receipt.roundingAdjustment))}</Text>
          </View>
        ) : null}
        <View style={styles.row}><Text style={[styles.rowLabel, { fontWeight: 700 }]}>Total Amount</Text><Text style={[styles.rowValue, { fontWeight: 700 }]}>{money(receipt.totalAmount)}</Text></View>

        {receipt.payments.length > 0 ? (
          <>
            <View style={styles.divider} />
            <Text style={styles.sectionTitle}>Payments</Text>
            {receipt.payments.map((payment) => (
              <View key={`${payment.label}-${payment.referenceNumber}-${payment.amount}`} style={styles.paymentRow}>
                <View style={styles.row}><Text style={styles.rowLabel}>{payment.label}</Text><Text style={styles.rowValue}>{money(payment.amount)}</Text></View>
                {payment.referenceNumber ? <Text style={styles.subtle}>Ref: {payment.referenceNumber}</Text> : null}
              </View>
            ))}
            {receipt.showTotalPaidRow ? (
              <View style={styles.row}><Text style={[styles.rowLabel, { fontWeight: 700 }]}>Total Paid</Text><Text style={[styles.rowValue, { fontWeight: 700 }]}>{money(receipt.totalPaid)}</Text></View>
            ) : null}
            {receipt.changeDue > 0 ? <View style={styles.row}><Text style={styles.rowLabel}>Change Due</Text><Text style={styles.rowValue}>{money(receipt.changeDue)}</Text></View> : null}
          </>
        ) : null}

        {receipt.gstSummary.length > 0 ? (
          <>
            <View style={styles.divider} />
            <Text style={styles.sectionTitle}>GST Summary</Text>
            <View style={styles.gstHeader}>
              <Text style={styles.gstColTax}>Tax</Text>
              <Text style={styles.gstCol}>Taxable</Text>
              <Text style={styles.gstCol}>CGST</Text>
              <Text style={styles.gstCol}>SGST</Text>
              <Text style={styles.gstCol}>IGST</Text>
            </View>
            {receipt.gstSummary.map((row) => (
              <View key={`${row.label}-${row.taxableAmount}`} style={styles.gstRow}>
                <Text style={styles.gstColTax}>{row.label}</Text>
                <Text style={styles.gstCol}>{parseFloat(row.taxableAmount || 0).toFixed(2)}</Text>
                <Text style={styles.gstCol}>{parseFloat(row.cgstAmount || 0).toFixed(2)}</Text>
                <Text style={styles.gstCol}>{parseFloat(row.sgstAmount || 0).toFixed(2)}</Text>
                <Text style={styles.gstCol}>{parseFloat(row.igstAmount || 0).toFixed(2)}</Text>
              </View>
            ))}
          </>
        ) : null}

        {receipt.salespersonName ? (
          <>
            <View style={styles.divider} />
            <Text>Salesperson: {receipt.salespersonName}</Text>
          </>
        ) : null}

        <View style={styles.divider} />
        <Text style={styles.sectionTitle}>Terms & Conditions</Text>
        <View style={styles.terms}>
          {receipt.terms.map((term) => (
            <Text key={term} style={styles.term}>• {term}</Text>
          ))}
        </View>
        {receipt.declarationText ? (
          <>
            <View style={styles.divider} />
            <Text style={styles.sectionTitle}>Declarations</Text>
            <Text style={styles.declaration}>{receipt.declarationText}</Text>
          </>
        ) : null}

        {receipt.footerMessage ? <Text style={styles.thanks}>{receipt.footerMessage}</Text> : null}
        {receipt.websiteHandle ? <HandleLine styles={styles} text={receipt.websiteHandle} url={websiteUrl(receipt.websiteHandle)} /> : null}
        {receipt.instagramHandle ? <HandleLine styles={styles} text={receipt.instagramHandle} url={instagramUrl(receipt.instagramHandle)} /> : null}
      </Page>
    </Document>
  );
};

export default ThermalReceiptPdf;
